/**
 * Plugin Toast Bridge
 *
 * Module-level bridge that allows the non-React PluginBridge class
 * to dispatch toasts through the React NotificationContext.
 *
 * Pattern:
 * 1. PluginBridge calls pluginToastBridge.success(msg) from its handler
 * 2. If connected, the real notification system fires; otherwise falls back to console
 * 3. PluginLoader (inside the React tree) calls connect() to wire the real system
 *
 * Wave 14e: Plugin System Hardening
 */

type ToastFn = (message: string) => void;

let _success: ToastFn = (msg) => console.log('[Plugin Toast]', msg);
let _error: ToastFn = (msg) => console.error('[Plugin Toast]', msg);
let _warning: ToastFn = (msg) => console.warn('[Plugin Toast]', msg);
let _info: ToastFn = (msg) => console.log('[Plugin Toast]', msg);
let _connected = false;

export const pluginToastBridge = {
  success: (msg: string) => _success(msg),
  error: (msg: string) => _error(msg),
  warning: (msg: string) => _warning(msg),
  info: (msg: string) => _info(msg),

  /** Whether the bridge is connected to the real notification system */
  get isConnected() {
    return _connected;
  },

  /**
   * Wire the bridge to the real toast system.
   * Called once from inside the React tree (PluginLoader).
   */
  connect(fns: {
    success: ToastFn;
    error: ToastFn;
    warning: ToastFn;
    info: ToastFn;
  }) {
    _success = fns.success;
    _error = fns.error;
    _warning = fns.warning;
    _info = fns.info;
    _connected = true;
  },

  /**
   * Disconnect (e.g., on unmount). Falls back to console.
   */
  disconnect() {
    _success = (msg) => console.log('[Plugin Toast]', msg);
    _error = (msg) => console.error('[Plugin Toast]', msg);
    _warning = (msg) => console.warn('[Plugin Toast]', msg);
    _info = (msg) => console.log('[Plugin Toast]', msg);
    _connected = false;
  },
};
